import { type Query, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { Discussions } from '@air/api';
import { type Account, type Board } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type ClipDiscussion } from '~/components/Annotations/shared/types';
import { getAccountKey } from '~/swr-hooks/account/useAccount';
import { type ClipDiscussionsCacheType } from '~/swr-hooks/discussions/types';
import { getCommentParams, insertCommentToDiscussion, isClipDiscussionsKey } from '~/swr-hooks/discussions/utils';

export type CreateCommentParams = {
  existingDiscussion: ClipDiscussion;
  newComment: string;
  boardId?: string;
  shortId?: string;
};

export const useCreateComment = () => {
  const queryClient = useQueryClient();

  const createComment = useCallback(
    async ({ existingDiscussion, newComment, shortId, boardId }: CreateCommentParams) => {
      const account = queryClient.getQueryData<Account>(getAccountKey());

      if (!account) {
        return;
      }

      try {
        const comment = getCommentParams(newComment, existingDiscussion);

        const queryKeyPredicate = ({ queryKey }: Query) =>
          isClipDiscussionsKey(queryKey) && queryKey[1].assetId === existingDiscussion.assetId;

        queryClient.setQueriesData<ClipDiscussionsCacheType>(
          {
            predicate: queryKeyPredicate,
          },
          (data) =>
            insertCommentToDiscussion({
              newComment: {
                ...comment,
                account,
              },
              data,
              discussionId: existingDiscussion.id,
            }),
        );

        await Discussions.reply({
          comment,
          // TODO: fix boardId typings once backend updates — boardId *is* optionally undefined
          // (e.g. private all-assets view and some share links). Cast avoids biome's non-null lint
          // while still passing undefined through to the API.
          // see: https://github.com/AirLabsTeam/backend/pull/2783
          boardId: boardId as Board['id'],
          shortId,
        });

        queryClient.invalidateQueries({ predicate: queryKeyPredicate });
      } catch (error) {
        throw reportErrorToBugsnag({
          error,
          context: 'Failed to create comment',
          metadata: { data: { account, existingDiscussion, newComment, shortId, boardId } },
        });
      }
    },
    [queryClient],
  );

  return {
    createComment,
  };
};
